"use client";

import {JSX, useMemo, useRef, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useKeenSlider} from "keen-slider/react";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {RentalCard, type RentalCardProps} from "@/entities/rental";

import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {TagBase, type TagBaseProps} from "@/shared/ui/data-display/tags/tag-base";
import {Typography} from "@/shared/ui/data-display/typography";
import {Spinner} from "@/shared/ui/feedback/spinner";
import {SpinnerOverlay} from "@/shared/ui/feedback/spinner-overlay";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";
import {scrollToPromise} from "@/shared/utils/scroll";

import "./our-property.scss";
import "keen-slider/keen-slider.min.css";

/**
 * Mobile Slider Component - separated to allow key-based remounting
 */
function MobilePropertiesSlider({
    properties,
    loading,
    noResultsText,
}: {
    properties: Array<RentalCardProps>;
    loading: {isFiltering: boolean; isInitial: boolean};
    noResultsText: string;
}): JSX.Element {
    const [currentSlide, setCurrentSlide] = useState(0);

    const [sliderRef, slider] = useKeenSlider({
        slides: {
            perView: 2,
            spacing: 16,
        },
        drag: true,
        loop: properties.length > 2,
        mode: "snap",
        rtl: false,
        slideChanged(s) {
            setCurrentSlide(s.track.details.rel);
        },
    });

    return (
        <>
            <div ref={sliderRef} className="keen-slider our-property__slider">
                <SpinnerOverlay loading={loading.isFiltering} size="large" variant="overlay" />
                {!loading.isInitial && properties.length === 0 && (
                    <div className="our-property__empty">
                        <Typography size={18} weight={600} color="title" as="p">
                            {noResultsText}
                        </Typography>
                    </div>
                )}
                {properties.map((property) => (
                    <div key={property.id} className="keen-slider__slide our-property__property-slide">
                        <RentalCard {...property} />
                    </div>
                ))}
            </div>
            {properties.length > 0 && !loading.isFiltering && (
                <div className="our-property__navigation">
                    <div className="our-property__dots">
                        {properties.map((_, idx) => (
                            <button
                                key={idx}
                                className={`our-property__dot ${currentSlide === idx ? "our-property__dot--active" : ""}`}
                                onClick={() => slider.current?.moveToIdx(idx)}
                                aria-label={`Go to slide ${idx + 1}`}
                            />
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

type OurPropertyProps = {
    properties: Array<RentalCardProps>;
    tags: Array<Omit<TagBaseProps, "onClick">>;
    loading: {
        isFiltering: boolean;
        isInitial: boolean;
        isPagination: boolean;
    };
    showButton: boolean;
    showReadLessButton: boolean;
    onToggleTag: (tagValue: string) => void;
    onLoadMore: () => void;
    onShowLess: () => void;
    space?: SpaceProps;
    anchorId?: string;
};

export function OurProperty({
    properties,
    tags,
    loading,
    showButton,
    showReadLessButton,
    onToggleTag,
    onLoadMore,
    onShowLess,
    space = {pt: 0},
    anchorId,
}: OurPropertyProps): JSX.Element {
    const {t} = useTranslation("common");
    const isMobile = useMatchMedia({maxWidth: 767});

    const [tagsSliderRef] = useKeenSlider({
        slides: {
            perView: "auto",
            spacing: 7,
        },
        breakpoints: {
            "(min-width: 768px)": {
                slides: {
                    perView: "auto",
                    spacing: 15,
                },
            },
        },
        drag: true,
        loop: false,
        mode: "snap",
        rtl: false,
    });

    const sectionVars = useSectionSpace(space);
    const sectionRef = useRef<HTMLElement>(null);

    // Create a unique key based on property IDs to force slider remount when data changes
    const sliderKey = useMemo(() => properties.map((p) => p.id).join("-"), [properties]);

    const handleShowLess = async (): Promise<void> => {
        if (!sectionRef.current) return;

        const offset = 120; // Offset for header/navigation
        const y = sectionRef.current.getBoundingClientRect().top + window.scrollY - offset;

        await scrollToPromise(y, {behavior: "smooth"});
        onShowLess();
    };

    return (
        <section ref={sectionRef} className="our-property" style={{...sectionVars}} id={anchorId}>
            <ContainerBase>
                <div className="our-property__tags-wrap">
                    {tags.length > 0 && (
                        <div ref={tagsSliderRef} className="keen-slider our-property__tags">
                            {tags.map((tag, index) => (
                                <div key={`${tag.value || tag.label}-${index}`} className="keen-slider__slide our-property__tag-slide">
                                    <TagBase
                                        label={tag.label}
                                        icon={tag.icon}
                                        value={tag.value}
                                        active={tag.active}
                                        onClick={() => onToggleTag(tag.value || tag.label)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {isMobile ? (
                    <MobilePropertiesSlider key={sliderKey} properties={properties} loading={loading} noResultsText={t("noResultsFound")} />
                ) : (
                    <div className="our-property__wrap" style={{position: "relative"}}>
                        <SpinnerOverlay loading={loading.isFiltering} size="large" variant="overlay" />
                        {!loading.isInitial && properties.length === 0 && (
                            <div className="our-property__empty">
                                <Typography size={18} weight={600} color="title" as="p">
                                    {t("noResultsFound")}
                                </Typography>
                            </div>
                        )}
                        {properties.map((property) => (
                            <RentalCard key={property.id} {...property} />
                        ))}
                    </div>
                )}

                {!isMobile && (showButton || loading.isPagination) && !loading.isFiltering && (
                    <div className="our-property__more">
                        <ButtonBase variant="secondary-outline" onClick={onLoadMore} size="xs-s" width="fit">
                            {loading.isPagination ? (
                                <Spinner size="small" />
                            ) : (
                                <>
                                    {t("showMore")}
                                    <SmartImage src={assetPath("/images/icons/arrow-black-down.svg")} alt="Arrow" width={12} height={10} />
                                </>
                            )}
                        </ButtonBase>
                    </div>
                )}

                {!isMobile && showReadLessButton && !loading.isFiltering && !loading.isPagination && (
                    <div className="our-property__more">
                        <ButtonBase variant="secondary-outline" onClick={() => void handleShowLess()} size="xs-s" width="fit">
                            {t("showLess")}
                            <SmartImage
                                src={assetPath("/images/icons/arrow-black-down.svg")}
                                alt="Arrow"
                                width={12}
                                height={10}
                                className="our-property__arrow our-property__arrow--up"
                            />
                        </ButtonBase>
                    </div>
                )}
            </ContainerBase>
        </section>
    );
}
